import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useWebgate } from '@xvia/webgate-connect';
import { getServerData } from '../util/get-server-data';
import { ICategory, getCategorys } from '../apis/categorys';
import { IDepartment, getDepartments } from '../apis/department';
import { Catalog } from '../apis/search-catalog';
import { useRedirectWarningModal } from './use-redirect-warning-modal';
import { DropdownData } from '@plataformaxvia/design-system';
import { extractQuery } from '../util/url';
import useQuery from './use-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useCatalogQuery } from './catalog-query';

export interface IFilter {
  searchQuery: string;
  selectedCategory: string;
  selectedDepartment: string;
  selectedTag: string;
  digital: boolean;
  scheduled: boolean;
  currentPage: number;
}

type ServicesFiltersContextType = {
  selectedCategory: string;
  categories: ICategory[];
  departments: IDepartment[];
  selectedDepartment: string;
  switchData: {
    id: string;
    label: string;
    checked: boolean;
    setChecked: React.Dispatch<React.SetStateAction<boolean>>;
    disabled: boolean;
  }[];
  dropdownData: DropdownData[];
  servicesCatalogList: Catalog[];
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  totalPages: number;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  search: string;
  title: string;
  element: React.ReactNode;
  totalServices: boolean;
  isRedirectWarningModalOpen: boolean;
  toggleRedirectWarningModal: () => void;
  handleRedirectWarningModalConfirm: () => void;
  handleInfoClick: (slug: string) => void;
  handleRequestClick: (link: string) => void;
  handleSearchClick: () => void;
  handleSearchClear: () => void;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  filters: IFilter;
  resetFilters: () => void;
  selectedTag: string;
  isLoading: boolean;
  isTotalServicesLoading: boolean;
};

export const ServicesFiltersContext = createContext<ServicesFiltersContextType>(
  {} as ServicesFiltersContextType
);

const itemsPerPage = 8;

export const ServicesFiltersContextProvider: React.FC<PropsWithChildren<{}>> =
  ({ children }) => {
    const { accessToken, serverData, portalConnect } = useWebgate();
    const history = useHistory();
    const queryParams = useQuery();

    const [categories, setCategories] = useState<ICategory[]>([]);
    const [departments, setDepartments] = useState<IDepartment[]>([]);
    const [search, setSearch] = useState('');

    const [categoryDropdownVisibility, setCategoryDropdownVisibility] =
      useState(false);
    const [departmentDropdownVisibility, setDepartmentDropdownVisibility] =
      useState(false);

    const getInitialFilters = useCallback(
      () => ({
        searchQuery: queryParams.get('search') || extractQuery() || '',
        selectedCategory: '',
        selectedDepartment: '',
        selectedTag: queryParams.get('tag') || '',
        digital: Boolean(queryParams.get('digital')) ?? false,
        scheduled: Boolean(queryParams.get('scheduled')) ?? false,
        currentPage: 1,
      }),
      [queryParams]
    );

    const [filters, setFilters] = useState(getInitialFilters);

    const resetFilters = useCallback(() => {
      setFilters(getInitialFilters());
    }, [getInitialFilters]);

    useEffect(() => {
      resetFilters();
    }, [history, getInitialFilters, resetFilters]);

    const initialCatalogData = serverData?.top
      ? {
          items: serverData.top,
        }
      : getServerData('top', null);

    const { data, isLoading } = useCatalogQuery({
      filters,
      accessToken,
      itemsPerPage,
      initialData: initialCatalogData,
    });

    const servicesCatalogList = useMemo(() => data?.items ?? [], [data?.items]);
    const totalPages = data?.totalPages ?? 1;
    const isTotalServicesLoading = data?.isTotalLoading ?? false;

    useEffect(() => {
      if (data?.shouldClampCurrentPage && filters.currentPage > totalPages) {
        setFilters((prev) => ({
          ...prev,
          currentPage: totalPages,
        }));
      }
    }, [data?.shouldClampCurrentPage, filters.currentPage, totalPages]);

    useEffect(() => {
      if (servicesCatalogList.length > 0) {
        const departmentId = departments.find(
          (dept) => dept.slug === filters.selectedDepartment
        )?.externalId;

        const servicesIds = servicesCatalogList.map((item: { slug: string }) =>
          item.slug?.split('-')?.pop()
        );

        if (
          departmentId &&
          servicesIds.length &&
          typeof window !== 'undefined'
        ) {
          window.localStorage.setItem(
            'selectedDepartmentData',
            JSON.stringify({
              id: departmentId,
              servicesIds,
            })
          );
        } else if (typeof window !== 'undefined') {
          window.localStorage.removeItem('selectedDepartmentData');
        }
      } else if (typeof window !== 'undefined') {
        window.localStorage.removeItem('selectedDepartmentData');
      }
    }, [servicesCatalogList, filters.selectedDepartment, departments]);

    const uniqueCategories: ICategory[] = [];
    const seenSlugs = new Set<string>();

    categories.forEach((category) => {
      if (!seenSlugs.has(category.slug)) {
        seenSlugs.add(category.slug);
        uniqueCategories.push(category);
      }
    });

    const uniqueDepartments: IDepartment[] = [];

    departments.forEach((department) => {
      if (!seenSlugs.has(department.slug)) {
        seenSlugs.add(department.slug);
        uniqueDepartments.push(department);
      }
    });

    const createSwitchData = (scheduled: boolean, digital: boolean) => [
      {
        id: '1',
        label: 'Agendamento Online',
        ariaLabel: 'Filtrar por agendamento online',
        checked: scheduled,
        setChecked: () =>
          setFilters((prev) => ({
            ...prev,
            scheduled: !scheduled,
            currentPage: 1,
          })),
        disabled: false,
      },
      {
        id: '2',
        label: 'Serviço Digital',
        ariaLabel: 'Filtrar por serviço digital',
        checked: digital,
        setChecked: () =>
          setFilters((prev) => ({
            ...prev,
            digital: !digital,
            currentPage: 1,
          })),
        disabled: false,
      },
    ];

    const switchData = createSwitchData(filters.scheduled, filters.digital);

    const toggleCategorySelection = (slug: string) => {
      setFilters((prev) =>
        prev.selectedCategory === slug
          ? { ...prev, selectedCategory: '', selectedTag: '', currentPage: 1 }
          : {
              ...prev,
              searchQuery: '',
              selectedCategory: slug,
              selectedDepartment: '',
              selectedTag: '',
              currentPage: 1,
            }
      );
    };

    const toggleDepartmentSelection = (slug: string) => {
      setFilters((prev) =>
        prev.selectedDepartment === slug
          ? { ...prev, selectedDepartment: '', selectedTag: '', currentPage: 1 }
          : {
              ...prev,
              searchQuery: '',
              selectedCategory: '',
              selectedDepartment: slug,
              selectedTag: '',
              currentPage: 1,
            }
      );
    };

    const formatLabel = (name: string) =>
      name
        .split(' ')
        .map((word) =>
          word.length <= 2
            ? word.toLowerCase()
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');

    const dropdownData = [
      {
        id: 'categoria',
        title: 'Por Categoria',
        active: categoryDropdownVisibility,
        onToggle: () => setCategoryDropdownVisibility((prev) => !prev),
        dropdownItems: uniqueCategories.map((category) => ({
          id: category.id,
          icon: <FontAwesomeIcon size="sm" icon={category.icon as IconProp} />,
          label: formatLabel(category.name),
          checked: filters.selectedCategory === category.slug,
          setChecked: () => toggleCategorySelection(category.slug),
        })),
      },
      {
        id: 'departamento',
        title: 'Por Secretaria e Órgão',
        active: departmentDropdownVisibility,
        onToggle: () => setDepartmentDropdownVisibility((prev) => !prev),
        dropdownItems: uniqueDepartments.map((department) => ({
          id: department.id,
          label: formatLabel(department.name),
          checked: filters.selectedDepartment === department.slug,
          setChecked: () => toggleDepartmentSelection(department.slug),
        })),
      },
    ];

    const handleSearchClick = (customSearch?: string) => {
      setFilters((prev) => ({
        ...prev,
        searchQuery: customSearch ?? search,
        selectedCategory: '',
        selectedDepartment: '',
        selectedTag: '',
        currentPage: 1,
      }));
    };

    const handleSearchClear = () => {
      setSearch('');
      handleSearchClick('');
    };

    const {
      toggleRedirectWarningModal,
      handleRedirectWarningModalConfirm,
      isRedirectWarningModalOpen,
      setCurrentServiceRedirectLink,
    } = useRedirectWarningModal();

    const handleRequestClick = useCallback(
      (link: string) => {
        if (!link.startsWith('/app')) {
          setCurrentServiceRedirectLink(link);
          toggleRedirectWarningModal();
        } else {
          portalConnect?.redirectTo(link);
        }
      },
      [toggleRedirectWarningModal, portalConnect, setCurrentServiceRedirectLink]
    );

    const handleInfoClick = useCallback(
      (slug: string) => {
        const pathname = `/carta-de-servicos/${slug}`;
        history.push({ pathname, search: history.location.search });

        window.parent.dispatchEvent(
          new CustomEvent('catalog:navigation', {
            detail: {
              pathname: `/app/catalog${pathname}`,
              search: '',
              source: 'catalog',
            },
          })
        );
      },
      [history]
    );

    const getFiltersData = useCallback(async () => {
      try {
        const categories = await getCategorys();
        setCategories(categories);
        const departments = await getDepartments();
        setDepartments(departments);
      } catch (err) {
        console.error(err);
      }
    }, []);

    useEffect(() => {
      getFiltersData();
    }, [getFiltersData]);

    return (
      <ServicesFiltersContext.Provider
        value={{
          categories,
          selectedCategory: filters.selectedCategory,
          departments,
          selectedDepartment: filters.selectedDepartment,
          switchData,
          dropdownData,
          servicesCatalogList,
          setCurrentPage: (page) => {
            setFilters((prev) => ({
              ...prev,
              currentPage:
                typeof page === 'number' ? page : page(filters.currentPage),
            }));
          },
          currentPage: filters.currentPage,
          totalPages,
          setSearch,
          search,
          title: 'Carta de Serviços',
          element: null,
          totalServices: true,
          isRedirectWarningModalOpen,
          toggleRedirectWarningModal,
          handleRedirectWarningModalConfirm,
          handleInfoClick,
          handleRequestClick,
          handleSearchClick,
          handleSearchClear,
          setFilters,
          filters,
          resetFilters,
          selectedTag: filters.selectedTag,
          isLoading,
          isTotalServicesLoading,
        }}
      >
        {children}
      </ServicesFiltersContext.Provider>
    );
  };

export const useServicesFilters = () => useContext(ServicesFiltersContext);
